import React, { useState, useRef, useEffect } from 'react';
import { Image as ImageIcon, ChevronLeft, ChevronRight, Sparkles, Home, Calendar, Building2, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';

// Before/After Slider Component with drag functionality
const BeforeAfterSlider = ({ beforeImage, afterImage, language }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-72 md:h-80 overflow-hidden rounded-t-xl cursor-ew-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* After Image (Background) */}
      <div className="absolute inset-0">
        <img
          src={afterImage}
          alt="After"
          className="w-full h-full object-cover"
          draggable="false"
        />
        {/* DEPOIS label */}
        <div className="absolute top-3 right-3 bg-[#1a5c3a] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {language === 'pt' ? 'DEPOIS' : 'AFTER'}
        </div>
      </div>

      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={beforeImage}
          alt="Before"
          className="w-full h-full object-cover"
          draggable="false"
        />
        {/* ANTES label */}
        <div className="absolute top-3 left-3 bg-gray-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {language === 'pt' ? 'ANTES' : 'BEFORE'}
        </div>
      </div>

      {/* Slider Line */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Slider Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-[#1a5c3a]">
          <ChevronLeft size={14} className="text-[#1a5c3a] -mr-1" />
          <ChevronRight size={14} className="text-[#1a5c3a] -ml-1" />
        </div>
      </div>

      {/* Instruction overlay - shows briefly */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none">
        {language === 'pt' ? '← Arraste para comparar →' : '← Drag to compare →'}
      </div>
    </div>
  );
};

// Service categories with icons
const serviceCategories = [
  { 
    id: 'house', 
    en: 'House Cleaning', 
    pt: 'Limpeza Doméstica',
    icon: Home
  },
  { 
    id: 'deep', 
    en: 'Deep Cleaning', 
    pt: 'Limpeza Profunda',
    icon: Sparkles
  },
  { 
    id: 'tenancy', 
    en: 'End of Tenancy', 
    pt: 'Fim de Arrendamento',
    icon: Calendar
  },
  { 
    id: 'commercial', 
    en: 'Commercial Cleaning', 
    pt: 'Limpeza Comercial',
    icon: Building2
  },
  { 
    id: 'airbnb', 
    en: 'Airbnb Cleaning', 
    pt: 'Limpeza Airbnb',
    icon: MapPin
  }
];

// Gallery items - organized by category
const galleryItems = [
  // House Cleaning - Shot Glass Collection
  {
    id: 1,
    category: 'house',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/ra8qgrun_cleaner2.webp',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/0w33e8yb_cleaner1.jpeg',
    titleEn: 'Collection Display Clean',
    titlePt: 'Limpeza de Estante de Colecção',
    descEn: 'Careful cleaning and reorganization of collectibles display cabinet.',
    descPt: 'Limpeza cuidadosa e reorganização de estante de coleccionáveis.',
    location: 'Dublin'
  },
  // House Cleaning - Kitchen Counter
  {
    id: 2,
    category: 'house',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/l4ulkipd_deep5.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/na3t2sq0_deep6.jpeg',
    titleEn: 'Kitchen Counter Clean',
    titlePt: 'Limpeza de Bancada de Cozinha',
    descEn: 'Full kitchen sink area cleaning - dishes, counters and organization.',
    descPt: 'Limpeza completa da área do lava-loiça - loiça, bancadas e organização.',
    location: 'Dublin'
  },
  // House Cleaning - Small Kitchen
  {
    id: 3,
    category: 'house',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/0xk0vchp_cleaner3.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/6qx3s97k_cleaner4.jpeg',
    titleEn: 'Small Kitchen Clean',
    titlePt: 'Limpeza de Cozinha Pequena',
    descEn: 'Complete kitchen cleaning - counters, appliances and organization.',
    descPt: 'Limpeza completa da cozinha - bancadas, eletrodomésticos e organização.',
    location: 'Dublin'
  },
  // Deep Cleaning - Kitchen Behind Stove
  {
    id: 4,
    category: 'deep',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/yo9ueg7x_deep1.webp',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/lrf0bfba_deep2.webp',
    titleEn: 'Behind the Stove Clean',
    titlePt: 'Limpeza Atrás do Fogão',
    descEn: 'Deep cleaning of hidden areas behind the stove - complete degreasing.',
    descPt: 'Limpeza profunda das áreas escondidas atrás do fogão - desengorduramento completo.',
    location: 'Dublin'
  },
  // Deep Cleaning - Oven
  {
    id: 5,
    category: 'deep',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/sn5413vv_deep3.webp',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/iyepln2z_deep4.webp',
    titleEn: 'Oven Deep Clean',
    titlePt: 'Limpeza Profunda do Forno',
    descEn: 'Complete oven restoration - removed burnt grease and carbon buildup.',
    descPt: 'Restauração completa do forno - remoção de gordura queimada e resíduos de carbono.',
    location: 'Dublin'
  },
  // Deep Cleaning - Sofa
  {
    id: 6,
    category: 'deep',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/qr18r4aq_deep7.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/hyriwets_deep8.jpeg',
    titleEn: 'Sofa Deep Clean',
    titlePt: 'Limpeza Profunda do Sofá',
    descEn: 'Complete sofa cleaning - removed all debris and deep cleaned fabric.',
    descPt: 'Limpeza completa do sofá - remoção de todos os detritos e limpeza profunda do tecido.',
    location: 'Dublin'
  },
  // Deep Cleaning - Kitchen Faucet
  {
    id: 7,
    category: 'deep',
    before: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/1o2dgy0h_deep9.jpeg',
    after: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/809vjxbu_deep10.jpeg',
    titleEn: 'Kitchen Faucet Deep Clean',
    titlePt: 'Limpeza Profunda da Torneira',
    descEn: 'Limescale and calcium deposit removal - restored shine to chrome faucet.',
    descPt: 'Remoção de calcário e depósitos de cálcio - brilho restaurado na torneira cromada.',
    location: 'Dublin'
  }
];

const GalleryPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Read category from URL parameter
  const searchParams = new URLSearchParams(location.search);
  const urlCategory = searchParams.get('category');
  
  const [activeCategory, setActiveCategory] = useState(urlCategory || 'all');
  const [selectedItem, setSelectedItem] = useState(null);

  // Update category when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category) {
      setActiveCategory(category);
    }
  }, [location.search]);

  // Update URL when category changes
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'all') {
      navigate('/ie/gallery');
    } else {
      navigate(`/ie/gallery?category=${category}`);
    }
  };

  const filteredItems = activeCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  const getCategoryName = (categoryId) => {
    const cat = serviceCategories.find(c => c.id === categoryId);
    return cat ? (language === 'pt' ? cat.pt : cat.en) : categoryId;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#1a5c3a] via-[#1a5c3a] to-[#15472d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20">
            <ImageIcon size={18} className="text-[#d4a84b]" />
            <span className="text-sm font-medium">
              {language === 'pt' ? 'Trabalhos Realizados' : 'Completed Projects'}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
            {language === 'pt' ? 'Galeria ' : 'Before & After '}
            <span className="text-[#d4a84b]">
              {language === 'pt' ? 'Antes e Depois' : 'Gallery'}
            </span>
          </h1>
          <p className="text-lg md:text-xl font-body max-w-2xl mx-auto text-white/80">
            {language === 'pt' 
              ? 'Veja a transformação dos nossos projetos. Arraste para comparar o antes e depois.'
              : 'See the transformation of our projects. Drag to compare before and after.'}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-0 z-20 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === 'all'
                  ? 'bg-[#1a5c3a] text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              {language === 'pt' ? 'Todos' : 'All'}
            </button>
            {serviceCategories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#1a5c3a] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  <IconComponent size={16} />
                  <span className="hidden sm:inline">{language === 'pt' ? cat.pt : cat.en}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredItems.length === 0 ? (
            <div className="text-center py-16" data-testid="no-photos">
              <ImageIcon size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-body">
                {language === 'pt' 
                  ? 'Nenhuma foto disponível nesta categoria. Em breve!'
                  : 'No photos available in this category. Coming soon!'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  data-testid={`gallery-item-${item.id}`}
                >
                  {/* Before/After Slider */}
                  <BeforeAfterSlider
                    beforeImage={item.before}
                    afterImage={item.after}
                    language={language}
                  />
                  
                  {/* Info */}
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1a5c3a] bg-[#1a5c3a]/10 px-2.5 py-1 rounded-full">
                        {React.createElement(
                          serviceCategories.find(c => c.id === item.category)?.icon || Home,
                          { size: 12 }
                        )}
                        {getCategoryName(item.category)}
                      </span>
                      <span className="text-xs text-gray-400">{item.location}</span>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-gray-900 mb-1">
                      {language === 'pt' ? item.titlePt : item.titleEn}
                    </h3>
                    <p className="text-sm text-gray-600 font-body">
                      {language === 'pt' ? item.descPt : item.descEn}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-16 text-center bg-gradient-to-r from-[#1a5c3a] to-[#15472d] rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              {language === 'pt' 
                ? 'Quer uma transformação assim?'
                : 'Want a transformation like this?'}
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              {language === 'pt'
                ? 'Peça já o seu orçamento grátis e veja a diferença que podemos fazer na sua casa ou escritório.'
                : 'Get your free quote now and see the difference we can make in your home or office.'}
            </p>
            <a
              href="/ie/quote"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4a84b] text-white font-semibold rounded-lg hover:bg-[#c49a40] transition-all shadow-lg hover:shadow-xl"
              data-testid="gallery-cta"
            >
              <Sparkles size={20} />
              {language === 'pt' ? 'Pedir Orçamento Grátis' : 'Get Free Quote'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
